import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-base font-semibold">{label}</Label>
    <Input {...props} className="min-h-[56px] rounded-xl border-2 border-stone-300 px-4 text-lg focus-visible:ring-4 focus-visible:ring-primary/30" />
  </div>
);

export default function Auth() {
  const { t, lang, setLang } = useLang();
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [mode, setMode] = useState(sp.get("mode") === "signup" ? "signup" : "login");
  const [form, setForm] = useState({ first_name: "", display_name: "", email: "", password: "",
    instagram: "", tiktok: "", facebook: "", youtube: "" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ref = sp.get("ref") || localStorage.getItem("bt_ref");

  const doSignup = async () => {
    if (!form.first_name || !form.email || form.password.length < 6) {
      toast.error(lang === "fr" ? "Remplis prénom, email et un mot de passe (6+ caractères)." : "Fill in first name, email and a password (6+ chars).");
      return;
    }
    if (!accepted) { toast.error(t("consent_required")); return; }
    setBusy(true);
    try {
      const socials = { instagram: form.instagram, tiktok: form.tiktok, facebook: form.facebook, youtube: form.youtube };
      await api.post("/auth/register", {
        first_name: form.first_name, display_name: form.display_name, email: form.email,
        password: form.password, language: lang, socials, referred_by: ref || null,
      });
      setMode("verify");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "EMAIL_EXISTS" ? (lang === "fr" ? "Cet email est déjà utilisé." : "This email is already used.") : apiError(d, t("err_generic")));
    } finally { setBusy(false); }
  };

  const doVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { email: form.email, code });
      loginWithToken(data.token, data.user);
      toast.success("🎉");
      nav("/earn");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "WRONG_CODE" ? (lang === "fr" ? "Code incorrect." : "Wrong code.") : apiError(d, t("err_generic")));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-code", { email: form.email });
      toast.success(t("resend"));
    } catch (e) { toast.error(t("err_generic")); }
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
      loginWithToken(data.token, data.user);
      nav(data.user.role === "admin" ? "/admin" : "/earn");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.detail === "NOT_VERIFIED") {
        setMode("verify");
      } else {
        toast.error(d === "INVALID_CREDENTIALS" ? (lang === "fr" ? "Email ou mot de passe incorrect." : "Wrong email or password.") : apiError(d, t("err_generic")));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl border-2 border-stone-200 p-6 sm:p-9 shadow-sm">

        {mode === "signup" && (
          <>
            <h1 className="font-head font-black text-3xl mb-2">{t("welcome")}</h1>
            <p className="text-stone-600 mb-6">{t("welcome_sub")}</p>
            <div className="space-y-4" data-testid="signup-form">
              <div>
                <Label className="text-base font-semibold">{t("lang_q")}</Label>
                <div className="flex gap-3 mt-1.5">
                  {["fr", "en"].map((l) => (
                    <button key={l} type="button" onClick={() => setLang(l)} data-testid={`signup-lang-${l}`}
                      className={`flex-1 min-h-[52px] rounded-xl border-2 font-bold transition-colors ${lang === l ? "border-primary bg-primary/10 text-primary" : "border-stone-200"}`}>
                      {l === "fr" ? "🇫🇷 Français" : "🇬🇧 English"}
                    </button>
                  ))}
                </div>
              </div>
              <Field label={t("first_name")} value={form.first_name} onChange={set("first_name")} data-testid="input-firstname" />
              <Field label={t("display_name")} value={form.display_name} onChange={set("display_name")} data-testid="input-displayname" />
              <Field label={t("email")} type="email" value={form.email} onChange={set("email")} data-testid="input-email" />
              <Field label={t("password")} type="password" value={form.password} onChange={set("password")} data-testid="input-password" />
              <div className="pt-1">
                <Label className="text-base font-semibold">{t("socials_q")}</Label>
                <p className="text-sm text-stone-500 mb-2">{t("socials_hint")}</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Instagram" value={form.instagram} onChange={set("instagram")} data-testid="input-instagram" className="rounded-xl border-2 border-stone-200 min-h-[48px]" />
                  <Input placeholder="TikTok" value={form.tiktok} onChange={set("tiktok")} className="rounded-xl border-2 border-stone-200 min-h-[48px]" />
                  <Input placeholder="Facebook" value={form.facebook} onChange={set("facebook")} className="rounded-xl border-2 border-stone-200 min-h-[48px]" />
                  <Input placeholder="YouTube" value={form.youtube} onChange={set("youtube")} className="rounded-xl border-2 border-stone-200 min-h-[48px]" />
                </div>
              </div>
              <div className="flex items-start gap-3 py-1">
                <Checkbox id="consent" checked={accepted} onCheckedChange={setAccepted} data-testid="consent-checkbox" className="mt-1" />
                <label htmlFor="consent" className="text-sm text-stone-600 cursor-pointer">
                  {t("consent_text")}{" "}
                  <Link to="/legal/terms" target="_blank" className="text-primary underline">ToS</Link> ·{" "}
                  <Link to="/legal/privacy" target="_blank" className="text-primary underline">Privacy</Link>
                </label>
              </div>
              <Button onClick={doSignup} disabled={busy} data-testid="signup-submit"
                className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[60px] text-lg font-bold">
                {busy ? t("loading") : t("create_account")} <ArrowRight className="ml-2" size={20} />
              </Button>
              <button onClick={() => setMode("login")} data-testid="switch-login" className="w-full text-stone-600 font-semibold py-2">{t("have_account")}</button>
            </div>
          </>
        )}

        {mode === "verify" && (
          <div data-testid="verify-form">
            <h1 className="font-head font-black text-3xl mb-2">{t("verify_title")}</h1>
            <p className="text-stone-600 mb-6">{t("verify_sub")}</p>
            <div className="flex justify-center mb-6">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="w-12 h-14 text-2xl border-2 rounded-xl" />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={doVerify} disabled={busy || code.length < 6} data-testid="verify-submit"
              className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[60px] text-lg font-bold">
              {busy ? t("loading") : t("verify_btn")}
            </Button>
            <button onClick={resend} data-testid="resend-code" className="w-full text-stone-600 font-semibold py-3">{t("resend")}</button>
          </div>
        )}

        {mode === "login" && (
          <div data-testid="login-form">
            <h1 className="font-head font-black text-3xl mb-6">{t("login_title")}</h1>
            <div className="space-y-4">
              <Field label={t("email")} type="email" value={form.email} onChange={set("email")} data-testid="login-email" />
              <Field label={t("password")} type="password" value={form.password} onChange={set("password")} data-testid="login-password"
                onKeyDown={(e) => e.key === "Enter" && doLogin()} />
              <Button onClick={doLogin} disabled={busy} data-testid="login-submit"
                className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[60px] text-lg font-bold">
                {busy ? t("loading") : t("login")}
              </Button>
              <button onClick={() => setMode("signup")} data-testid="switch-signup" className="w-full text-stone-600 font-semibold py-2">{t("no_account")}</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
